import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, OnInit } from '@angular/core';

import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { AuthService } from './auth.service';
import { Observable, throwError } from 'rxjs';
import { LoggerService } from './logger.service';

@Injectable({
  providedIn: 'root'
})
export class DrupalRESTService implements OnInit {

  baseURL = environment.hostUrl;
  // csrfToken;

  _httpHeaders: HttpHeaders;
  _httpHeadersUpload: HttpHeaders;

  constructor(
    private http: HttpClient,
    private _authService: AuthService
  ) {
    //this._csrfToken()
  }

  httpHeaders() {
    // const csrfToken = localStorage.getItem('csrf_token');
    const csrfToken = this._authService.csrf_token;

    if (csrfToken) {
      this._httpHeaders = new HttpHeaders({
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken,
      });
    } else {
      this._httpHeaders = new HttpHeaders({
        'Content-Type': 'application/json',
      });
    }
  }

  ngOnInit(): void {
    // this._csrfToken();
  }

  /**
   * Utility to build our HTTPParams object.
   *
   * @param params
   * @returns HttpParams
   */
  private _httpParams(params?: { parameter: string, value: any }[]) {
    let httpParams = new HttpParams();
    let addedParams = new Map<string, boolean>();

    // Check if _format is already specified in params
    const hasFormatParam = params?.some(p => p.parameter === '_format');

    // Add _format=json by default if not already specified
    if (!hasFormatParam) {
      httpParams = httpParams.append('_format', 'json');
      addedParams.set('_format', true);
    }

    // Add the studio reference to the main request
    if (this._authService.selectedStudio) {
      // Use the selected studio for API requests
      if (this._authService.selectedStudio.id !== undefined && this._authService.selectedStudio.id !== null) {
        const studioId = this._authService.selectedStudio.id.toString();
        httpParams = httpParams.append('field_studio_reference_target_id', studioId);
        httpParams = httpParams.append('field_studio_reference', studioId);
        // Track that we've added these parameters
        addedParams.set('field_studio_reference_target_id', true);
        addedParams.set('field_studio_reference', true);
        // httpParams = httpParams.append('XDEBUG_TRIGGER', '1');
      }
    } else if (this._authService.studios && this._authService.studios.length > 0) {
      // Fallback to first studio if no selected studio
      const studio = this._authService.studios[0];
      if (studio.id !== undefined && studio.id !== null) {
        const studioId = studio.id.toString();
        httpParams = httpParams.append('field_studio_reference_target_id', studioId);
        httpParams = httpParams.append('field_studio_reference', studioId);
        // Track that we've added these parameters
        addedParams.set('field_studio_reference_target_id', true);
        addedParams.set('field_studio_reference', true);
      }
    }

    // Inject studio reference on the entire data before iterating over params
    if (params) {
      params.forEach((param) => {
        if (param.value && typeof param.value === 'object') {
          this.injectStudioReference(param.value);
        }
      });
    }

    // Existing logic with check for duplicates
    if (typeof params !== 'undefined') {
      params.forEach((element) => {
        if (element.value !== undefined && element.value !== null) {
          // Only add the parameter if we haven't already added it
          if (!addedParams.has(element.parameter)) {
            httpParams = httpParams.append(element.parameter, element.value.toString());
            addedParams.set(element.parameter, true);
          }
        }
      });
    }

    return httpParams;
  }

  /**
   * Normalize endpoint query params so callers can safely pass params either
   * in the URL or through HttpParams without creating duplicates.
   */
  private _normalizeEndpoint(
    endpoint: string,
    params?: { parameter: string, value: any }[]
  ): { endpoint: string, params: { parameter: string, value: any }[] } {
    const [path, queryString] = endpoint.split('?', 2);
    const normalizedPath = path === '/api/dayview/autocomplete'
      ? '/api_rest/v1/autocomplete'
      : path;
    const mergedParams: { parameter: string, value: any }[] = [];
    const seen = new Set<string>();

    if (queryString) {
      const searchParams = new URLSearchParams(queryString);
      searchParams.forEach((value, key) => {
        if (!seen.has(key)) {
          mergedParams.push({ parameter: key, value });
          seen.add(key);
        }
      });
    }

    (params || []).forEach((param) => {
      if (!seen.has(param.parameter)) {
        mergedParams.push(param);
        seen.add(param.parameter);
      }
    });

    return {
      endpoint: normalizedPath,
      params: mergedParams
    };
  }

  private _logPatchFailure(endpoint: string, body: any, error: any, params?: { parameter: string, value: any }[]) {
    const bodyKeys = body && typeof body === 'object' ? Object.keys(body) : [];
    LoggerService.error('[DrupalRESTService] PATCH request failed', {
      endpoint,
      params,
      status: error?.status,
      statusText: error?.statusText,
      message: error?.message,
      selectedStudioId: this._authService.selectedStudio?.id ?? null,
      bodyKeys
    });
  }

  private needsStudioReference(obj: any): boolean {
    return obj && obj.hasOwnProperty('type') && obj['type'] === 'attendees';
  }

  private injectStudioReference(obj: any) {
    LoggerService.log("Checking object: ", obj);

    if (this.needsStudioReference(obj)) {
      LoggerService.log("Injecting studio reference into: ", obj);
      if (this._authService.selectedStudio && this._authService.selectedStudio.id) {
        obj['field_studio_reference_target_id'] = this._authService.selectedStudio.id.toString();
        obj['field_studio_reference'] = this._authService.selectedStudio.id.toString();
      } else if (this._authService.studios && this._authService.studios.length > 0) {
        const studio = this._authService.studios[0];
        if (studio.id) {
          obj['field_studio_reference_target_id'] = studio.id.toString();
          obj['field_studio_reference'] = studio.id.toString();
        }
      }
    }

    for (const key in obj) {
      if (obj[key] && typeof obj[key] === 'object') {
        if (Array.isArray(obj[key])) {
          for (const item of obj[key]) {
            this.injectStudioReference(item);
          }
        } else {
          this.injectStudioReference(obj[key]);
        }
      }
    }
  }

  public _csrfToken(): Observable<string> {
    let endpoint = "/session/token";
    let httpParams = this._httpParams();

    // Return an Observable which completes once the token is fetched and set
    return this.http.get(this.baseURL + endpoint, {
      headers: this._httpHeaders,
      params: httpParams,
      responseType: 'text' as 'json'  // Cast to 'json' to satisfy TypeScript's type expectations
    }).pipe(
      tap((token: string) => {
        this._authService.csrf_token = token;
        this.httpHeaders(); // this method updates the headers with the new token
      })
    );
  }

  /**
   * Standard HTTP GET request to Drupal.
   *
   * @param endpoint
   * @param params
   * @returns Observable
   */
  httpGET(endpoint: string, params?: { parameter: string, value: string }[]) {
    const normalized = this._normalizeEndpoint(endpoint, params);
    // Get HTTPParams object
    let httpParams = this._httpParams(normalized.params);

    return this.http.get(this.baseURL + normalized.endpoint, {
      headers: this._httpHeaders,
      params: httpParams,
      withCredentials: true
    })
      .pipe(
        map(data => {
          return data;
        },
          (error: string) => this.logError(error)
        )
      )
  }

  /**
   * Standard HTTP POST to Drupal.
   *
   * @param endpoint
   * @param body
   * @param params
   * @returns Observable
   */
  httpPOST(endpoint: string, body: any, params?: { parameter: string, value: string }[]): Observable<any> {
    const normalized = this._normalizeEndpoint(endpoint, params);
    // Get HTTPParams object
    let httpParams = this._httpParams(normalized.params);

    // Check if body is FormData (file upload)
    if (body instanceof FormData) {
      // Add studio reference to FormData if not already present
      if (!body.has('field_studio_reference') && this._authService.selectedStudio) {
        body.append('field_studio_reference', this._authService.selectedStudio.id.toString());
      }
    } else {
      // For JSON requests, inject studio reference into body
      this._injectStudioReferenceIntoBody(body);
    }

    // Get new token and then send POST request
    return this._csrfToken().pipe(
      switchMap(() => {
        let headers = this._httpHeaders;

        // If it's a file upload, don't set Content-Type header
        if (body instanceof FormData) {
          headers = headers.delete('Content-Type');
        }

        return this.http.post(
          this.baseURL + normalized.endpoint,
          body,
          {
            observe: 'response',
            headers: headers,
            params: httpParams,
            withCredentials: true
          }
        ).pipe(
          map(response => response.body)
        );
      })
    );
  }

  /**
   * Standard HTTP PATCH to Drupal.
   *
   * @param endpoint
   * @param body
   * @param params
   * @returns Observable
   */
  httpPATCH(endpoint: string, body: any, params?: { parameter: string, value: string }[], options?: { skipStudioReference?: boolean }): Observable<any> {
    const normalized = this._normalizeEndpoint(endpoint, params);
    // Get HTTPParams object
    let httpParams = this._httpParams(normalized.params);

    // Modify the body to include studio references. Callers that PATCH a
    // non-studio-scoped resource (e.g. the current user's own account settings)
    // can opt out so the auto-injected field_studio_reference never reaches a
    // resource that would misinterpret it.
    if (!options?.skipStudioReference) {
      this._injectStudioReferenceIntoBody(body);
    }

    // Get new token and then send PATCH request
    return this._csrfToken().pipe(
      switchMap(() => {
        return this.http.patch(
          this.baseURL + normalized.endpoint,
          body,
          {
            observe: 'response',
            headers: this._httpHeaders,
            params: httpParams,
            withCredentials: true
          }
        ).pipe(
          map(response => response.body), // This line maps the full HTTP response to its body
          catchError((error) => {
            this._logPatchFailure(normalized.endpoint, body, error, normalized.params);
            return throwError(() => error);
          })
        );
      })
    );
  }

  /**
   * Standard HTTP DELETE to Drupal.
   *
   * @param endpoint
   * @param body
   * @param params
   * @returns Observable
   */
  httpDELETE(endpoint: string, body?: { parameter: string, value: string }[], params?: { parameter: string, value: string }[]): Observable<any> {
    const normalized = this._normalizeEndpoint(endpoint, params);
    // Get HTTPParams object
    let httpParams = this._httpParams(normalized.params);

    // Modify the body to include studio references
    this._injectStudioReferenceIntoBody(body);

    // Get new token and then send DELETE request
    return this._csrfToken().pipe(
      switchMap(() => {
        return this.http.delete(
          this.baseURL + normalized.endpoint,
          {
            observe: 'response',
            headers: this._httpHeaders,
            params: httpParams,
            withCredentials: true
          }
        );
      })
    );
  }

  /**
   * Support for uploading files in Drupal.
   */
  uploadFile(entity_type_id: string, bundle: string, field_name: string, fileName: string, body) {
    let url = `/file/upload/${entity_type_id}/${bundle}/${field_name}`;

    // Get CSRF token and then upload the file
    return this._csrfToken().pipe(
        switchMap(() => {
            let headers = new HttpHeaders({
                'Content-Type': 'application/octet-stream',
                'X-CSRF-Token': this._authService.csrf_token,
                'Content-Disposition': `file; filename="${fileName}"`
            });

            LoggerService.log('Uploading file: ', body, headers);
            return this.http.post(this.baseURL + url, body, {
                headers: headers,
                withCredentials: true
            });
        })
    );
}

  /**
   *
   * @param route
   * @param httpParams
   * @returns Observable
   */
  downloadFile(route: string, params) {
    const normalized = this._normalizeEndpoint(route, params);
    let httpParams = this._httpParams(normalized.params);
    // If _format is already in the route URL, remove it from httpParams to avoid conflict
    return this.http.get<Blob>(this.baseURL + normalized.endpoint, {
      headers: this._httpHeaders,
      params: httpParams,
      withCredentials: true,
      observe: 'response',
      responseType: 'blob' as 'json'
    })
  }

  private logError(error: string) {
    // do nothing :(
    LoggerService.log(error);
  }


  private _injectStudioReferenceIntoBody(body: any) {
      if (!body) {
        return; // exit early if body is falsy
      }

      // If there's a selected studio, inject its reference into the body
      if (this._authService.selectedStudio && this._authService.selectedStudio.id) {
          body['field_studio_reference_target_id'] = this._authService.selectedStudio.id;
          body['field_studio_reference'] = this._authService.selectedStudio.id;
      }
      // Fallback to first studio if no selected studio
      else if (this._authService.studios && this._authService.studios.length > 0) {
          const firstStudio = this._authService.studios[0];
          body['field_studio_reference_target_id'] = firstStudio.id;
          body['field_studio_reference'] = firstStudio.id;
      }

      // Iterate through the body and find nested entities to inject the studio reference
      for (const key in body) {
          if (body[key] && typeof body[key] === 'object') {
              this._injectStudioReferenceIntoNestedEntity(body[key]);
          }
      }
  }

  private _injectStudioReferenceIntoNestedEntity(entity: any) {
    if (this.needsStudioReference(entity)) {
      // If there's a selected studio, use it
      if (this._authService.selectedStudio && this._authService.selectedStudio.id) {
        entity['field_studio_reference_target_id'] = this._authService.selectedStudio.id;
        entity['field_studio_reference'] = this._authService.selectedStudio.id;
      }
      // Fallback to first studio if no selected studio
      else if (this._authService.studios && this._authService.studios.length > 0) {
        const firstStudio = this._authService.studios[0];
        entity['field_studio_reference_target_id'] = firstStudio.id;
        entity['field_studio_reference'] = firstStudio.id;
      }
    }

    // Recursive case: Dive deeper into nested objects/arrays
    for (const key in entity) {
      if (entity[key] && typeof entity[key] === 'object') {
        if (Array.isArray(entity[key])) {
          for (const item of entity[key]) {
            this._injectStudioReferenceIntoNestedEntity(item);
          }
        } else {
          this._injectStudioReferenceIntoNestedEntity(entity[key]);
        }
      }
    }
  }

}
